import { eventNames, useCoreEvents } from '@carvana/core-events';
import { useCallback, useContext, useMemo } from 'react';
import {
  ToastActions,
  ToastViews
} from '~/components/common/FavoritesToastContent/types';
import useDisplayFavoritesToast from '~/components/common/FavoritesToastContent/useDisplayFavoritesToast';
import UserContext from '~/context/common/UserContext';
import { sendSourcedFavoriteMessageToNative } from '~/native/common/dispatchers';
import { addFavorite } from '~/services/common';
import { logError } from '~/utilities/loggers';
import type { FavoritesArguments, LifeCycleFn } from './types';
import useLogInAndFavorite from './useLogInAndFavorite';

export type Options = {
  beforeAddFavorite?: LifeCycleFn;
  onAddFavorite?: LifeCycleFn;
};

const dummyFn = () => {};

function useAddFavorite({
  beforeAddFavorite = dummyFn,
  onAddFavorite = dummyFn
}: Options = {}) {
  const { emit } = useCoreEvents();
  const {
    favorites: { setData }
  } = useContext(UserContext);
  const displayFavoritesToast = useDisplayFavoritesToast();

  return useCallback(
    async (args: FavoritesArguments) => {
      const {
        subscribeToNotifications = false,
        vehicleId,
        nativeEventSource = ''
      } = args;

      try {
        beforeAddFavorite(args);

        const { data: vehicles } = await addFavorite({
          subscribeToNotifications,
          vehicleId
        });

        if (nativeEventSource)
          sendSourcedFavoriteMessageToNative(
            'add',
            {
              stockNumber: args.stockNumber ?? 0,
              vehicleId: args.vehicleId
            },
            nativeEventSource
          );

        setData(vehicles);

        emit(eventNames.favorites.mfe.refresh, { source: 'app', vehicles });
        displayFavoritesToast(ToastActions.add, ToastViews.vehicles);

        onAddFavorite(args);
      } catch (err) {
        logError(err, { location: 'Add favorites' });
      }
    },
    [beforeAddFavorite, displayFavoritesToast, emit, onAddFavorite, setData]
  );
}

export default function useAddFavoriteFlow(
  opts: Options
): (args: FavoritesArguments) => Promise<void> {
  const addFavoriteVehicle = useAddFavorite(opts);
  const loginAndAddFavorite = useLogInAndFavorite();

  return useMemo(
    () => loginAndAddFavorite(addFavoriteVehicle),
    [addFavoriteVehicle, loginAndAddFavorite]
  );
}
